import { useMemo } from "react";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { Module, PATH_PROJECTS } from "@/constants";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ProjectService } from "@/modules/projects/service/project";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { ReadProject } from "@/types/project";

export const useProjectActions = (projectId: string, project?: ReadProject) => {
  const { t } = useTranslation();
  const permissions = useModulePermissions(Module.Projects);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const projectCapabilities = useMemo(() => {
    const can_update = permissions.can_update;
    const can_delete = permissions.can_delete;
    return {
      canRequestApproval:
        can_update &&
        permissions.projects_request_approval &&
        project?.can_request_approval,
      canApprove:
        can_update && permissions.projects_approve && project?.can_be_approved,
      canReject:
        can_update && permissions.projects_reject && project?.can_be_rejected,
      canClose:
        can_update && permissions.projects_close && project?.can_be_closed,
      canDelete: can_delete && project?.can_be_deleted,
    };
  }, [
    permissions.can_update,
    permissions.can_delete,
    permissions.projects_request_approval,
    permissions.projects_approve,
    permissions.projects_reject,
    permissions.projects_close,
    project?.can_request_approval,
    project?.can_be_approved,
    project?.can_be_rejected,
    project?.can_be_closed,
    project?.can_be_deleted,
  ]);

  const {
    canRequestApproval,
    canApprove,
    canReject,
    canClose,
    canDelete: can_delete,
  } = projectCapabilities;

  const requestApprovalMutation = useMutationWithErrorHandling({
    mutationFn: () => ProjectService.requestApproval(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Projects, projectId] });
      toast({ title: t("projects.actions.requestApproval.success") });
    },
    errorTitle: t("projects.actions.requestApproval.error"),
  });

  const approveMutation = useMutationWithErrorHandling({
    mutationFn: () => ProjectService.approve(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Projects, projectId] });
      toast({ title: t("projects.actions.approve.success") });
    },
    errorTitle: t("projects.actions.approve.error"),
  });

  const rejectMutation = useMutationWithErrorHandling({
    mutationFn: (comments: string[]) =>
      ProjectService.reject(projectId, comments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Projects, projectId] });
      toast({ title: t("projects.actions.reject.success") });
    },
    errorTitle: t("projects.actions.reject.error"),
  });

  const closeMutation = useMutationWithErrorHandling({
    mutationFn: () => ProjectService.close(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Projects, projectId] });
      toast({ title: t("projects.actions.close.success") });
    },
    errorTitle: t("projects.actions.close.error"),
  });

  const deleteMutation = useMutationWithErrorHandling({
    mutationFn: () => ProjectService.delete(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Projects] });
      toast({ title: t("projects.actions.delete.success") });
      navigate(PATH_PROJECTS);
    },
    errorTitle: t("projects.actions.delete.error"),
  });

  return useMemo(
    () => ({
      canRequestApproval,
      canApprove,
      canReject,
      canClose,
      can_delete,
      requestApproval: requestApprovalMutation,
      approve: approveMutation,
      reject: rejectMutation,
      close: closeMutation,
      delete: deleteMutation,
    }),
    [
      canRequestApproval,
      canApprove,
      canReject,
      canClose,
      can_delete,
      requestApprovalMutation,
      approveMutation,
      rejectMutation,
      closeMutation,
      deleteMutation,
    ],
  );
};
